import React, { useState } from 'react'
import { Image, Dialog, Input, Toast } from 'antd-mobile';
import API_ROUTES from '../../api';
import { colorList, opponentColorList } from './color';
import httpClient from '../../shared/api/httpClient';
import Icon from '../../icon';
import { CheckOutline } from 'antd-mobile-icons';
import './gameTitleTeam.css';
import { CAPTAIN_WORKFLOW_STAGE, getCaptainWorkflow } from '../../features/match/model/captainWorkflow';

// Internal component to handle dialog state updates
const TeamEditContent = ({ initialName, initialColor, onUpdate, colors = colorList, extendedColors = false }) => {
    const [name, setName] = useState(initialName);
    const [color, setColor] = useState(initialColor);

    const handleColorSelect = (val) => {
        setColor(val);
        onUpdate({ name, color: val });
    };

    const handleNameChange = (val) => {
        setName(val);
        onUpdate({ name: val, color });
    };

    // Helper to get actual color value for displaystyle
    const getColorValue = (cVal) => {
        if (cVal === 'red') return '#ef4444';
        if (cVal === 'green') return '#10b981';
        return cVal;
    };

    return (
        <div className="team-edit-body">
            <div className="team-edit-header">
                <div className="team-edit-avatar-ring" style={{ borderColor: getColorValue(color) }}>
                    <div style={{ width: '50px', height: '50px', borderRadius: '50%', background: getColorValue(color), opacity: 0.2 }}></div>
                </div>
                <span className="team-edit-title">编辑队伍信息</span>
            </div>

            <div className="team-edit-field">
                <span className="team-edit-label">队伍名称</span>
                <Input
                    className="team-edit-input"
                    placeholder='请输入队伍名称'
                    value={name}
                    onChange={handleNameChange}
                />
            </div>

            <div className="team-edit-field">
                <span className="team-edit-label">队服颜色</span>
                <div className={`team-edit-color-circles ${extendedColors ? 'team-edit-color-circles--extended' : ''}`}>
                    {colors.map((item) => {
                        const isSelected = color === item.value;
                        const displayColor = getColorValue(item.value);
                        return (
                            <div
                                key={item.value}
                                className={`team-edit-color-circle ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleColorSelect(item.value)}
                                role='button'
                                aria-label={`选择${item.name || '队服颜色'}`}
                            >
                                <div
                                    className="color-circle-swatch"
                                    style={{ backgroundColor: displayColor }}
                                >
                                    {isSelected && <CheckOutline className="color-circle-check" />}
                                </div>
                                <span className="color-circle-label">{item.name}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const GameTitleTeam = ({ gameDetail, getGameDetail, type, gameTime }) => {
    const userInfo = localStorage.getItem("memberInfo")
    const memberInfo = userInfo ? JSON.parse(userInfo) : null;
    const namingPending = Number(gameDetail?.gameType) === 3 && Number(gameDetail?.statusInfo ?? gameDetail?.status) === 1
        && getCaptainWorkflow(gameDetail).stage === CAPTAIN_WORKFLOW_STAGE.TEAM_NAMING;
    const isInternalPreparing = Number(gameDetail?.gameType) === 3
        && Number(gameDetail?.statusInfo ?? gameDetail?.status) === 1;

    const getLogoInfo = (team) => {
        // 年会外战队伍没有上传队徽时展示队名占位，不复用科朋队徽。
        if (Number(gameDetail?.gameType) === 4) {
            const logo = team === 1 ? gameDetail?.greenTeamLogo : gameDetail?.redTeamLogo;
            const teamType = team === 1 ? gameDetail?.greenTeamType : gameDetail?.redTeamType;
            return logo ? logo + Icon.ImageFooter : (Number(teamType) === 1 ? '' : Icon.KEPENG_LOGO);
        }
        if (team === 1) {
            if (gameDetail?.greenTeamLogo) {
                return gameDetail?.greenTeamLogo + Icon.ImageFooter
            } else {
                return Icon.KEPENG_LOGO
            }
        } else if (team === 2) {
            if ([1, 2].includes(gameDetail?.gameType) && gameDetail?.redCaptain?.image) {
                return gameDetail?.redCaptain?.image
            } else if (gameDetail?.redTeamLogo) {
                return gameDetail?.redTeamLogo + Icon.ImageFooter
            } else {
                return Icon.KEPENG_LOGO
            }
        }
    }

    const renderTeamAvatar = (team) => {
        const logo = getLogoInfo(team);
        return logo ? <Image src={logo} alt={getTeamName(team)} width={60} height={60} fit='contain' style={{ borderRadius: '50%' }} />
            : <span className="versus-avatar-name" title={getTeamName(team)}>{getTeamName(team).slice(0, 4)}</span>;
    }

    const getTeamName = (teamIndex) => {
        // 内讧赛赛前优先展示已取的队名，未取名时才用固定队长的首字简称。
        if (isInternalPreparing) {
            const savedName = teamIndex === 1 ? gameDetail?.greenName : gameDetail?.redName;
            if (String(savedName || '').trim()) return savedName.trim();
            const captainName = teamIndex === 1 ? gameDetail?.greenCaptain?.name : gameDetail?.redCaptain?.name;
            if (captainName) return `${captainName.charAt(0)}队`;
        }
        if (namingPending) return teamIndex === 1 ? 'A队' : 'B队';
        if (teamIndex === 1) {
            // 正式赛、活动赛的左侧固定为科朋主队，避免显示创建比赛时的临时名称。
            if ([1, 2].includes(gameDetail?.gameType)) return '华西科朋足球队';
            if (gameDetail?.greenName) return gameDetail.greenName;
            if (gameDetail?.greenCaptain?.name) return gameDetail.greenCaptain.name.charAt(0) + '队';
            return 'A队';
        }
        if (teamIndex === 2) {
            if (gameDetail?.redName) return gameDetail.redName;
            if (gameDetail?.redCaptain?.name) return gameDetail.redCaptain.name.charAt(0) + '队';
            return 'B队';
        }
        return 'Team';
    }

    const getColorHex = (colorName) => {
        if (colorName === 'red') return '#ef4444';
        if (colorName === 'green') return '#10b981';
        return colorName || '#86909C';
    };

    const canEditTeam = (teamIndex) => {
        if (!memberInfo) return false;
        // 第一阶段只使用一次提交两队名称的表单，不开放顶部单队编辑。
        if (namingPending) return false;
        if (memberInfo.admin === 1) return true;
        const firstTeamCaptainId = gameDetail?.greenCaptainId || gameDetail?.greenCaptain || '';
        const isTeamPreparing = gameDetail?.status === 1 || gameDetail?.statusInfo === 1;
        // 报名结束后的第一个队伍队长允许维护顶部两个队伍的名称和队服颜色。
        return isTeamPreparing && [1, 2].includes(teamIndex) && String(memberInfo.id) === String(firstTeamCaptainId);
    };

    const showEditDialog = (teamIndex) => {
        const isGreen = teamIndex === 1;
        if (!canEditTeam(teamIndex)) return;
        const useOpponentColors = teamIndex === 2 && [1, 2].includes(gameDetail?.gameType);
        // 顶部显示队长简称时，编辑表单仍读取已保存的正式队名。
        const currentName = (isGreen ? gameDetail?.greenName : gameDetail?.redName) || getTeamName(teamIndex);
        const currentColor = isGreen ? (gameDetail?.greenColor || 'green') : (gameDetail?.redColor || 'red');

        // Mutable data object to capture state from the dialog component
        let editData = { name: currentName, color: currentColor };

        Dialog.confirm({
            className: 'team-edit-dialog',
            content: (
                <TeamEditContent
                    initialName={currentName}
                    initialColor={currentColor}
                    colors={useOpponentColors ? opponentColorList : colorList}
                    extendedColors={useOpponentColors}
                    onUpdate={(data) => { editData = data; }}
                />
            ),
            onConfirm: () => {
                Toast.show({ icon: 'loading', duration: 0 });
                httpClient.post(API_ROUTES.game_color, {
                    gameId: gameDetail?.id,
                    memberId: memberInfo?.id,
                    team: teamIndex,
                    teamName: editData.name,
                    teamColor: editData.color
                }, null)
                    .then(function (response) {
                        if (response.data?.code !== 200) {
                            Toast.show({ icon: 'fail', content: response.data?.msg || '修改失败' });
                            return;
                        }
                        getGameDetail();
                        Toast.show({ icon: 'success', content: '修改成功！' });
                    })
                    .catch(function (error) {
                        Toast.show({ icon: 'fail', content: error?.response?.data?.msg || '修改失败，请重试' });
                    });
            }
        });
    };

    // 优先读取比赛详情中的队服颜色；接口暂未返回时使用低饱和蓝、橙作为安全回退色。
    const greenColor = getColorHex(gameDetail?.greenColor || '#5b8def');
    const redColor = getColorHex(gameDetail?.redColor || '#e58a62');
    const isExternalMatch = [1, 2].includes(gameDetail?.gameType);
    const canEditExternalOpponent = isExternalMatch && canEditTeam(2);
    // 顶部卡片只读取比赛详情中的展示字段，不改变原有报名和编辑逻辑。
    // 对战阵容直接展示接口的 type 字段，兼容“12V12”和数字 12 两种返回格式。
    const matchupType = String(gameDetail?.type ?? '').trim();
    // 普通比赛保留原来的对阵占位，年会比赛才展示赛节数。
    const matchupText = matchupType
        ? (/[vV]/.test(matchupType) ? matchupType : `${matchupType} v ${matchupType}`)
        : (Number(gameDetail?.gameType) === 4 ? `${Number(gameDetail?.gameCourse) || 1} 节赛` : '-- v --');

    return (
        <div>
            <div className="game-title-team-container">
                <div
                    className="versus-glass-card"
                    style={{
                        '--left-team-color': greenColor,
                        '--right-team-color': redColor
                    }}
                >
                    <div className="versus-layout">
                        {/* Team A (Green) */}
                        <div
                            className="versus-team-side"
                            style={{ '--team-color': greenColor }}
                            onClick={() => {
                                if (!isExternalMatch) showEditDialog(1)
                            }}
                        >
                            <div className="versus-avatar-ring" style={{ '--team-color': greenColor }}>
                                {renderTeamAvatar(1)}
                            </div>
                            <span className="versus-team-name">{getTeamName(1)}</span>
                        </div>

                        {/* Center Info */}
                        <div className={`versus-center ${type === 1 ? 'versus-center--live' : ''}`}>
                            {type === 1 ? (
                                <>
                                    <div className="versus-live-badge">
                                        <div className="versus-live-dot"></div>
                                        LIVE
                                    </div>
                                    <span className="versus-score vs-score-pop">
                                        {gameDetail?.greenGoal} : {gameDetail?.redGoal}
                                    </span>
                                    <span className="versus-time-live">
                                        {gameTime || '00:00'}
                                    </span>
                                </>
                            ) : (
                                <>
                            <div className="versus-badge-shield">
                                <span className="versus-badge-text">VS</span>
                            </div>
                                    <div className="versus-energy-line left"></div>
                                    <div className="versus-energy-line right"></div>
                                    <span className="versus-start-time">{gameDetail?.startTime || '--:--'}</span>
                                    <span className="versus-matchup-text">{matchupText}</span>
                                </>
                            )}
                        </div>

                        {/* Team B (Red) */}
                        <div
                            className="versus-team-side"
                            style={{ '--team-color': redColor }}
                            onClick={() => {
                                if (!isExternalMatch) showEditDialog(2)
                            }}
                        >
                            <div
                                className={`versus-avatar-ring ${canEditExternalOpponent ? 'versus-avatar-ring--editable' : ''}`}
                                style={{ '--team-color': redColor }}
                                role={canEditExternalOpponent ? 'button' : undefined}
                                aria-label={canEditExternalOpponent ? '编辑对手队伍信息' : undefined}
                                tabIndex={canEditExternalOpponent ? 0 : undefined}
                                onClick={(event) => {
                                    if (!canEditExternalOpponent) return
                                    event.stopPropagation()
                                    showEditDialog(2)
                                }}
                                onKeyDown={(event) => {
                                    if (!canEditExternalOpponent || !['Enter', ' '].includes(event.key)) return
                                    event.preventDefault()
                                    showEditDialog(2)
                                }}
                            >
                                {renderTeamAvatar(2)}
                            </div>
                            <span className="versus-team-name">{getTeamName(2)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default GameTitleTeam;
